import React, { useState, useEffect } from 'react';
import { api, formatApiError } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Trash, PencilSimple, DotsThree, Eye, FilePdf } from '@phosphor-icons/react';
import { toast } from 'sonner';

import { LetterTemplateEditor, TEMPLATE_TYPES } from '../components/letter-templates/LetterTemplateEditor';
import { LetterPreviewDialog, LetterGenerateDialog } from '../components/letter-templates/LetterDialogs';

export default function LetterTemplates() {
  const [templates, setTemplates] = useState([]);
  const [tags, setTags] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedClient, setSelectedClient] = useState('');
  const [previewContent, setPreviewContent] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [formData, setFormData] = useState({ name: '', template_type: 'loe', subject: '', content: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [templatesRes, tagsRes, clientsRes] = await Promise.all([
        api.get('/letter-templates'), api.get('/letter-templates/tags'), api.get('/clients'),
      ]);
      setTemplates(templatesRes.data); setTags(tagsRes.data.tags); setClients(clientsRes.data);
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      if (editingTemplate) {
        const { data } = await api.put(`/letter-templates/${editingTemplate.id}`, formData);
        setTemplates(templates.map((t) => (t.id === editingTemplate.id ? data : t)));
        toast.success('Template updated successfully');
      } else {
        const { data } = await api.post('/letter-templates', formData);
        setTemplates([data, ...templates]); toast.success('Template created successfully');
      }
      closeDialog();
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try { await api.delete(`/letter-templates/${id}`); setTemplates(templates.filter((t) => t.id !== id)); toast.success('Template deleted'); }
    catch (error) { toast.error(formatApiError(error)); }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({ name: template.name, template_type: template.template_type, subject: template.subject || '', content: template.content });
    setDialogOpen(true);
  };

  const closeDialog = () => { setDialogOpen(false); setEditingTemplate(null); setFormData({ name: '', template_type: 'loe', subject: '', content: '' }); };

  const handlePreview = async (template) => {
    if (!selectedClient) { toast.error('Please select a client first'); return; }
    try {
      const { data } = await api.post('/letter-templates/preview', { template_id: template.id, client_id: selectedClient });
      setPreviewContent(data); setSelectedTemplate(template); setPreviewDialogOpen(true);
    } catch (error) { toast.error(formatApiError(error)); }
  };

  const handleGenerate = async () => {
    if (!selectedTemplate || !selectedClient) return;
    setGenerating(true);
    try {
      const { data } = await api.post('/letter-templates/generate-pdf', { template_id: selectedTemplate.id, client_id: selectedClient });
      toast.success('PDF generated successfully!'); setGenerateDialogOpen(false);
      try {
        const response = await api.get(`/generated-letters/${data.letter_id}/download`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const a = document.createElement('a'); a.href = url; a.download = data.filename;
        document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      } catch { toast.error('Failed to download generated letter'); }
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setGenerating(false); }
  };

  const getTypeLabel = (type) => TEMPLATE_TYPES.find((t) => t.value === type)?.label || type;

  return (
    <div className="space-y-6" data-testid="letter-templates-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="heading-2 mb-1">Letter Templates</h1>
          <p className="text-muted-foreground">Create and manage letter templates with auto-fill tags</p>
        </div>
        <LetterTemplateEditor
          open={dialogOpen} onOpenChange={setDialogOpen} editingTemplate={editingTemplate}
          formData={formData} setFormData={setFormData} tags={tags}
          submitting={submitting} onSubmit={handleSubmit} onClose={closeDialog}
        />
      </div>

      <Card className="border border-border">
        <CardContent className="py-4">
          <div className="flex items-center gap-4">
            <Label className="whitespace-nowrap">Select Client for Preview/Generate:</Label>
            <Select value={selectedClient} onValueChange={setSelectedClient}>
              <SelectTrigger className="w-64" data-testid="client-select"><SelectValue placeholder="Choose a client" /></SelectTrigger>
              <SelectContent>
                {clients.map((client) => (<SelectItem key={client.id} value={client.id}>{client.name} {client.company_name ? `(${client.company_name})` : ''}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="border border-border">
        <CardHeader className="pb-4">
          <CardTitle className="heading-4">Your Templates</CardTitle>
          <CardDescription>{templates.length} templates available</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div></div>
          ) : templates.length === 0 ? (
            <div className="text-center py-12"><p className="text-muted-foreground">No templates created yet</p><p className="text-sm text-muted-foreground mt-1">Create your first template to start generating letters</p></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="table-header"><TableHead>Name</TableHead><TableHead>Type</TableHead><TableHead>Created</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((template) => (
                  <TableRow key={template.id} data-testid={`template-row-${template.id}`}>
                    <TableCell className="font-medium">{template.name}</TableCell>
                    <TableCell><Badge variant="secondary">{getTypeLabel(template.template_type)}</Badge></TableCell>
                    <TableCell className="text-muted-foreground text-sm">{new Date(template.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => handlePreview(template)} disabled={!selectedClient} data-testid={`preview-template-${template.id}`}><Eye className="w-4 h-4 mr-1" /> Preview</Button>
                        <Button variant="default" size="sm" onClick={() => { setSelectedTemplate(template); setGenerateDialogOpen(true); }} disabled={!selectedClient} data-testid={`generate-template-${template.id}`}><FilePdf className="w-4 h-4 mr-1" /> Generate</Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><DotsThree className="w-4 h-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEdit(template)}><PencilSimple className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(template.id)} className="text-destructive"><Trash className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <LetterPreviewDialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen} previewContent={previewContent} />
      <LetterGenerateDialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen} selectedTemplate={selectedTemplate} clientName={clients.find(c => c.id === selectedClient)?.name} generating={generating} onGenerate={handleGenerate} />
    </div>
  );
}
